import {produce} from 'immer'

import deepEqual from 'fast-deep-equal'
import isEmpty from 'lodash/isEmpty'
import mapKeys from 'lodash/mapKeys'
import {createSelector} from 'reselect'

import type {State} from '../../../../reducers/types'
import type {PipelineId} from '../../../../types'
import {mapValues} from '../../../../utils/object'
import {ParameterInputPrefix} from '../contexts/ParametersControl/ParametersControlContext.const'
import type {NewPipeline} from '../services/pipelinesApi.types'
import type {Job, SuggestionType} from '../types'
import {getIsGitVcsRoot} from '../utils/getIsGitVcsRoot'
import {
  isValueConsistentWithArray,
  isValueNotEmptyMessage,
  isValueValidUrl,
  matchError,
} from '../utils/validators'
import type {GetValidators, ValidatorAdditionalProps} from '../utils/validators.types'

import {hasNoStoredPipelineYaml} from './EditPipelinePage.consts'
import {
  getBuildFeatureFormId,
  getDependencyName,
  getPipelineDependency,
  getPreformattingSettings,
  getVcsRootReference,
  parsePipeline,
  stringifyPipeline,
} from './EditPipelinePage.utils'
import {IntegrationType} from './SettingsSidebarContent/Integrations/Integrations.consts'
import {jobNameValidators} from './SettingsSidebarContent/JobSettingsSidebar/JobName/JobNameValidationError.consts'
import {errorValidators} from './SettingsSidebarContent/JobSettingsSidebar/JobSteps/JobStep/SmartDetectors/CliErrorMessage/useCliErrorMessage.consts'
import {pipelineNameValidators} from './SettingsSidebarContent/PipelineSettingsSidebar/PipelineName/PipelineNameValidationError.consts'
import {
  parameterNameValidators,
  parameterValueValidators,
} from './SettingsSidebarContent/PipelineSettingsSidebar/PipelineParameters/PipelineParameterForm/usePipelineParameterForm.const'
import {
  getRepositoryKey,
  ROOT_REPOSITORY_KEY,
} from './SettingsSidebarContent/PipelineSettingsSidebar/PipelineRepositories/PipelineRepository/PipelineRepository.utils'
import {
  secretNameValidators,
  secretValueValidators,
} from './SettingsSidebarContent/PipelineSettingsSidebar/PipelineSecrets/PipelineSecretForm/PipelineSecretForm.consts'
import {TriggerType} from './SettingsSidebarContent/PipelineSettingsSidebar/PipelineTriggers/PipelineTriggers.consts'
import {ALL_BRANCHES} from './SettingsSidebarContent/PipelineSettingsTriggerSidebar/PipelineOnChangesTriggerForm/PipelineOnChangesTriggerForm.const'
import type {PipelineError} from './slices/EditPipelinePage.slices'
import {MIN_PARALLELISM_COUNT} from './slices/EditPipelinePage.slices.consts'
import type {PipelineDraft, PipelineFormParams} from './slices/EditPipelinePage.slices.types'
import {FormType} from './slices/EditPipelinePage.slices.types'
import {
  deleteIfEmpty,
  getDebugEntry,
  getDependencyDetailsEntry,
  getPipelineDraftEntry,
  getPipelineFormKey,
  getYamlDraftEntry,
  getYamlValidityEntry,
  isDraftChanged,
} from './slices/EditPipelinePage.slices.utils'

type ResolvedSettings = NewPipeline & {
  stringificationError?: string
}

export const getPipeline = (state: State, id: string, branch: string) =>
  getPipelineDraftEntry(state.pipelines.pipelineDraft, id, branch)

export const getOriginal = (state: State, id: string, branch: string) =>
  getPipeline(state, id, branch)?.original

export const getOriginalPipelineSettings = (id: string, branch: string) => (state: State) =>
  getOriginal(state, id, branch)?.settings

export const getOriginalVersionedSettings = (id: string, branch: string) => (state: State) =>
  getOriginal(state, id, branch)?.versionedSettings

export const getDraft = (state: State, id: string, branch: string) =>
  getPipeline(state, id, branch)?.draft

export const getDeleted = (state: State, id: string, branch: string) =>
  getPipeline(state, id, branch)?.deleted

export const getRenamed = (state: State, id: string, branch: string) =>
  getPipeline(state, id, branch)?.renamed

export const getReordered = (state: State, id: string, branch: string) =>
  getPipeline(state, id, branch)?.reordered

export const getIsCollisionApi = (state: State, id: string, branch: string) =>
  getPipeline(state, id, branch)?.isCollisionApi

export const getDraftJobs = (state: State, id: string, branch: string) =>
  getDraft(state, id, branch)?.settings.jobs
export const getSteps = (state: State, id: string, branch: string, jobId: string) =>
  getDraftJobs(state, id, branch)?.[jobId]?.steps
export const getFeatures = (state: State, id: string, branch: string, jobId: string) =>
  getDraftJobs(state, id, branch)?.[jobId]?.features

export const getPipelineDependencyDetails = (state: State, id: PipelineId, branch: string) =>
  getDependencyDetailsEntry(state.pipelines.pipelineDependencyDetails, id, branch)

export const makePipelineErrorsSelector = () =>
  createSelector(
    [
      (state: State) => state.pipelines.pipelineErrors,
      (_: State, {pipelineId}: PipelineError) => pipelineId,
      (_: State, {branch}: PipelineError) => branch,
      (_: State, {jobId}: PipelineError) => jobId,
      (_: State, {fieldName}: PipelineError) => fieldName,
      (_: State, {message}: PipelineError) => message,
    ],
    (errors, pipelineId, branch, jobId, fieldName, message) =>
      errors.filter(matchError({pipelineId, branch, jobId, fieldName, message})),
  )

export const makeTotalCountPipelineErrorsSelector = () =>
  createSelector([makePipelineErrorsSelector()], errors => errors.length)

export const getPipelineErrorMessage = (filter: PipelineError) => (state: State) =>
  state.pipelines.pipelineErrors.find(matchError(filter))?.message

export const getJobName = (state: State, pipelineId: PipelineId, branch: string, id: string) =>
  getDraftJobs(state, pipelineId, branch)?.[id]?.name ?? id

export const getRunsOn = (jobId: string, id: string, branch: string) => (state: State) =>
  getDraft(state, id, branch)?.settings?.jobs?.[jobId]?.['runs-on']

export const getOriginalRunsOn = (jobId: string, id: string, branch: string) => (state: State) =>
  getOriginal(state, id, branch)?.settings?.jobs?.[jobId]?.['runs-on']

export const getJobAllowReuse = (jobId: string, id: string, branch: string) => (state: State) =>
  getDraft(state, id, branch)?.settings?.jobs?.[jobId]?.['allow-reuse'] !== false

export const isPipelineFormOpened = (state: State, {pipelineId, ...rest}: PipelineFormParams) =>
  state.pipelines.pipelineForm[pipelineId]?.[getPipelineFormKey(rest)]?.open
export const isPipelineFormAdd = (state: State, {pipelineId, ...rest}: PipelineFormParams) =>
  state.pipelines.pipelineForm[pipelineId]?.[getPipelineFormKey(rest)]?.isAdd

export const getSkippedSuggestions = (state: State, stepId: string) =>
  state.pipelines.suggestions.skippedSuggestions[stepId]

export const isSuggestionSkipped = (state: State, stepId: string, type: SuggestionType) =>
  getSkippedSuggestions(state, stepId)?.includes(type)

export const isSuccessMessageVisible = (state: State, stepId: string, type: SuggestionType) =>
  state.pipelines.suggestions.successMessages[stepId] === type

// Defined next to the draft entry helpers, because the pipelines reducer needs it too.
export {isDraftChanged}

export const isDraftSettingsChanged = createSelector(
  (state?: PipelineDraft) => {
    const {jobs, ...deleted} = state?.deleted ?? {}
    return isEmpty(deleted) ? null : deleted
  },
  (state?: PipelineDraft) => state?.renamed,
  (state?: PipelineDraft) => state?.added,
  (deleted, renamed, added) => Boolean(deleted || renamed || added),
)

const getResolvedSettingsFromDraft = createSelector(
  (state: PipelineDraft) => state?.draft,
  (state: PipelineDraft, params?: GetResolvedSettingsOptions) =>
    params?.excludeDeleted ? null : state?.deleted,
  (state: PipelineDraft) => state?.renamed,
  (state: PipelineDraft, params?: GetResolvedSettingsOptions) =>
    params?.excludeReordered ? null : state?.reordered,
  (draft, deleted, renamed, reordered): ResolvedSettings | null => {
    let stringificationError: string | undefined
    const result = produce(draft, pipeline => {
      if (pipeline != null) {
        if (pipeline.integrations != null) {
          if (deleted?.integrations != null) {
            pipeline.integrations = pipeline.integrations.filter(
              item => !deleted.integrations?.includes(item.id!),
            )
          }
          pipeline.integrations.forEach(({type, parameters}) => {
            switch (type) {
              case IntegrationType.DOCKER:
                parameters.repositoryUrl = parameters.repositoryUrl?.trim() ?? ''
                if (parameters.userName != null) {
                  parameters.userName = parameters.userName.trim()
                }
                break
              case IntegrationType.NPM:
                parameters.npmRegistryHost = parameters.npmRegistryHost?.trim() ?? ''
                if (parameters.npmRegistryScope != null) {
                  parameters.npmRegistryScope = parameters.npmRegistryScope.trim()
                }
                break
              default:
            }
          })
        }
        if (
          pipeline.additionalVcsRoots != null &&
          (deleted?.additionalVcsRoots?.length || deleted?.incompleteAdditionalVcsRoots?.length)
        ) {
          pipeline.additionalVcsRoots = pipeline.additionalVcsRoots.filter((root, index) => {
            const reference = getVcsRootReference(root)
            return reference
              ? !deleted?.additionalVcsRoots?.includes(reference)
              : !deleted?.incompleteAdditionalVcsRoots?.includes(index)
          })
        }
        const {settings} = pipeline
        if (settings.jobs != null) {
          deleted?.jobs?.forEach(id => {
            delete settings.jobs![id]
          })
          Object.entries(settings.jobs).forEach(([jobId, job]) => {
            if (deleted?.steps?.[jobId] != null || reordered?.steps?.[jobId] != null) {
              job.steps = job.steps
                ?.map((_, i) => {
                  const originalIndex = reordered?.steps?.[jobId]?.[i] ?? i
                  return {
                    value: job.steps![originalIndex],
                    isDeleted: deleted?.steps?.[jobId]?.includes(originalIndex),
                  }
                })
                .filter(({isDeleted}) => !isDeleted)
                .map(({value}) => value)
            }
            if (
              job.parallelism != null &&
              (!job.parallelism || job.parallelism < MIN_PARALLELISM_COUNT)
            ) {
              delete job.parallelism
            }
            if (job.repositories?.length && deleted?.additionalVcsRoots?.length) {
              job.repositories = job.repositories?.filter(repository => {
                const [url] = Object.keys(repository)

                return !deleted?.additionalVcsRoots?.includes(url)
              })

              if (!job.repositories?.length) {
                deleteIfEmpty(job, 'repositories')
              }
            }

            if (job.parameters != null) {
              deleted?.jobParameters?.forEach(name => {
                delete job.parameters![name]
              })
              if (renamed?.jobParameters != null) {
                job.parameters = mapKeys(
                  job.parameters,
                  (_, name) => renamed.jobParameters![name] ?? name,
                )
              }

              deleteIfEmpty(job, 'parameters')
            }

            if (job['files-publication'] != null) {
              job['files-publication'] = job['files-publication'].filter(
                (_, index) => !deleted?.filesPublication?.includes(`${jobId}_${index}`),
              )

              deleteIfEmpty(job, 'files-publication')
            }
            if (job.features?.length) {
              job.features = job.features.filter(feature => feature.deleted !== true)
              deleteIfEmpty(job, 'features')
            }
          })
        }
        if (settings.parameters != null) {
          deleted?.parameters?.forEach(name => {
            delete settings.parameters![name]
          })
          if (renamed?.parameters != null) {
            settings.parameters = mapKeys(
              settings.parameters,
              (_, name) => renamed.parameters![name] ?? name,
            )
          }
          deleteIfEmpty(settings, 'parameters')
        }
        if (settings['output-parameters'] != null) {
          deleted?.outputParameters?.forEach(name => {
            delete settings['output-parameters']![name]
          })
          if (renamed?.outputParameters != null) {
            settings['output-parameters'] = mapKeys(
              settings['output-parameters'],
              (_, name) => renamed.outputParameters![name] ?? name,
            )
          }
          deleteIfEmpty(settings, 'output-parameters')
        }
        if (settings.dependencies != null) {
          deleted?.dependencies?.forEach(dependencyId => {
            const index = settings?.dependencies?.findIndex(dependency =>
              getPipelineDependency(dependency, dependencyId),
            )
            if (index != null) {
              settings?.dependencies?.splice(index, 1)
            }
          })

          deleteIfEmpty(settings, 'dependencies')
        }
        if (settings.secrets != null) {
          deleted?.secrets?.forEach(name => {
            delete settings.secrets![name]
          })
          if (renamed?.secrets != null) {
            settings.secrets = mapKeys(
              settings.secrets,
              (_, name) => renamed.secrets![name] ?? name,
            )
          }

          deleteIfEmpty(settings, 'secrets')
        }

        if (pipeline.notifications != null && deleted?.notifications?.length) {
          pipeline.notifications = pipeline.notifications.filter(
            notification =>
              !deleted.notifications?.includes(
                pipeline.notifications.indexOf(notification).toString(),
              ),
          )
        }

        try {
          pipeline.yaml = stringifyPipeline(settings, {
            replacer: getPreformattingSettings,
            noRefs: true,
          })
        } catch (error) {
          stringificationError = error instanceof Error ? error.message : String(error)
        }
      }
    })

    if (!result) {
      return null
    }

    const returnValue: ResolvedSettings = {...result} as ResolvedSettings
    if (stringificationError) {
      returnValue.stringificationError = stringificationError
    }
    return returnValue
  },
)

export const getJobEnableDependencyCache =
  (jobId: string, id: string, branch: string) => (state: State) =>
    getDraft(state, id, branch)?.settings?.jobs?.[jobId]?.['enable-dependency-cache']

type GetResolvedSettingsOptions = {
  excludeDeleted?: boolean
  excludeReordered?: boolean
}

export const getResolvedSettings = (
  state: State,
  pipelineId: PipelineId,
  branch: string,
  params?: GetResolvedSettingsOptions,
) => getResolvedSettingsFromDraft(getPipeline(state, pipelineId, branch) ?? {}, params)

export const getHasEffectiveChangesFromOriginal = createSelector(
  (state: State, id: PipelineId, branch: string) => getResolvedSettings(state, id, branch),
  (state: State, id: PipelineId, branch: string) => getOriginal(state, id, branch),
  (state: State, id: PipelineId, branch: string) => state.pipelines.pipelineSecrets[id]?.[branch],
  (state: State, id: PipelineId, branch: string) =>
    state.pipelines.pipelineStepSecrets[id]?.[branch],
  (resolvedSettings, originalSettings, secrets, stepSecrets) => {
    const {yaml: updatedYaml, ...updated} = resolvedSettings ?? {}
    const {yaml: originalYaml, ...original} = originalSettings ?? {}
    return (
      !isEmpty(secrets) ||
      !isEmpty(stepSecrets) ||
      (!isEmpty(updated) && !deepEqual(updated, original))
    )
  },
)

/**
 * The draft has visual changes (deletions, renames, reorderings), but they cancel each other out,
 * so the resolved settings are equal to the saved ones and there is nothing to send to the server.
 */
export const getHasOnlyRevertedDraftChanges = (state: State, id: PipelineId, branch: string) =>
  isDraftChanged(getPipeline(state, id, branch)) &&
  !getHasEffectiveChangesFromOriginal(state, id, branch)

export const getHasNonYamlSettingsChanges = createSelector(
  (state: State, id: PipelineId, branch: string) => getResolvedSettings(state, id, branch),
  (state: State, id: PipelineId, branch: string) => getOriginal(state, id, branch),
  (resolvedSettings, originalSettings) => {
    const {yaml: _updatedYaml, settings: _updatedSettings, ...updated} = resolvedSettings ?? {}
    const {yaml: _originalYaml, settings: _originalSettings, ...original} = originalSettings ?? {}
    return !isEmpty(updated) && !deepEqual(updated, original)
  },
)

export const getYamlValidity = (state: State, id: PipelineId, branch: string) =>
  getYamlValidityEntry(state.pipelines.pipelineYamlValidity, id, branch)

export const getHasYamlSettingsChanges = createSelector(
  (state: State, id: PipelineId, branch: string) => getDraft(state, id, branch)?.settings,
  (state: State, id: PipelineId, branch: string) => getOriginal(state, id, branch)?.settings,
  (state: State, id: PipelineId, branch: string) => state.pipelines.pipelineSecrets[id]?.[branch],
  (state: State, id: PipelineId, branch: string) =>
    getYamlDraftEntry(state.pipelines.pipelineYamlDraft, id, branch)?.yaml,
  (draftSettings, originalSettings, secrets, yamlDraft) =>
    yamlDraft != null || !isEmpty(secrets) || !deepEqual(draftSettings, originalSettings),
)

export enum NonYamlSettingsCategory {
  INTEGRATIONS = 'Integrations',
  VCS_ROOTS = 'VCS roots',
  TRIGGERS = 'Triggers',
  NOTIFICATIONS = 'Notifications',
}

const NON_YAML_SETTINGS_CATEGORY_FIELDS: Record<NonYamlSettingsCategory, string[]> = {
  [NonYamlSettingsCategory.INTEGRATIONS]: ['integrations'],
  [NonYamlSettingsCategory.VCS_ROOTS]: ['vcsRoot', 'additionalVcsRoots'],
  [NonYamlSettingsCategory.TRIGGERS]: ['triggers'],
  [NonYamlSettingsCategory.NOTIFICATIONS]: ['notifications'],
}

export const getChangedNonYamlSettingsCategories = createSelector(
  (state: State, id: PipelineId, branch: string) =>
    getResolvedSettings(state, id, branch) as Record<string, unknown> | null,
  (state: State, id: PipelineId, branch: string) =>
    getOriginal(state, id, branch) as Record<string, unknown> | null,
  (resolved, original) =>
    Object.values(NonYamlSettingsCategory).filter(category =>
      NON_YAML_SETTINGS_CATEGORY_FIELDS[category].some(
        key => !deepEqual(resolved?.[key], original?.[key]),
      ),
    ),
)

const yamlSelector = createSelector(
  [
    (state: State, props: {pipelineId: PipelineId; branch: string}) =>
      getYamlValidity(state, props.pipelineId, props.branch)?.yaml,
    (state: State, props: {pipelineId: PipelineId; branch: string}) =>
      getYamlValidity(state, props.pipelineId, props.branch)?.isParsed,
    (state: State, props: {pipelineId: PipelineId; branch: string}) =>
      getResolvedSettings(state, props.pipelineId, props.branch),
  ],
  (invalidYaml, isParsed, resolved) => {
    if (!isParsed) {
      return invalidYaml ?? ''
    }
    if (!resolved?.settings) {
      return ''
    }
    if (resolved.stringificationError != null) {
      return invalidYaml ?? ''
    }
    return resolved.yaml ?? ''
  },
)

export const getYamlDraft = (state: State, id: PipelineId, branch: string) =>
  getYamlDraftEntry(state.pipelines.pipelineYamlDraft, id, branch)

const getOriginalYaml = createSelector(
  (state: State, pipelineId: PipelineId, branch: string) =>
    getOriginal(state, pipelineId, branch)?.settings,
  settings => {
    if (!settings) {
      return undefined
    }
    try {
      return stringifyPipeline(settings, {
        replacer: getPreformattingSettings,
        noRefs: true,
      })
    } catch (error) {
      return ''
    }
  },
)

export const getYaml = (state: State, pipelineId: PipelineId, branch: string) => {
  const {yaml = ''} = getOriginal(state, pipelineId, branch) ?? {}
  const yamlFromDraft = yamlSelector(state, {pipelineId, branch})
  return yamlFromDraft === getOriginalYaml(state, pipelineId, branch) ? yaml : yamlFromDraft
}

export const hasNoRepoYamlOnBranch = (
  state: State,
  pipelineId: PipelineId,
  branch: string,
): boolean => {
  const original = getOriginal(state, pipelineId, branch)
  return hasNoStoredPipelineYaml(original?.yaml, original?.versionedSettings)
}

export function getNewYaml(state: State, pipelineId: PipelineId, branch: string) {
  return getYamlDraft(state, pipelineId, branch)?.yaml ?? getYaml(state, pipelineId, branch)
}

export const isNoYamlInBranchState = (
  state: State,
  pipelineId: PipelineId,
  branch: string,
): boolean =>
  hasNoRepoYamlOnBranch(state, pipelineId, branch) &&
  getYamlDraft(state, pipelineId, branch) == null &&
  deepEqual(
    getDraft(state, pipelineId, branch)?.settings,
    getOriginal(state, pipelineId, branch)?.settings,
  )

export const getNewSettings = (state: State, pipelineId: PipelineId, branch: string) => {
  try {
    return parsePipeline(getNewYaml(state, pipelineId, branch))
  } catch {
    return {}
  }
}

export function getStringifiedDependencies(jobs?: Record<string, Job>) {
  const result: Record<string, string[]> = {}
  if (jobs != null) {
    Object.entries(jobs).forEach(([id, job]) => {
      result[id] = job.dependencies?.map(getDependencyName) ?? []
    })
  }
  return JSON.stringify(result)
}

export function getAllFormsToValidate(
  state: State,
  pipelineId: PipelineId,
  branch: string,
): PipelineFormParams[] {
  const {draft, deleted} = getPipeline(state, pipelineId, branch) ?? {}
  if (draft == null) {
    return []
  }
  const {settings, vcsRoot, additionalVcsRoots = [], integrations: draftIntegrations = []} = draft
  const {
    jobs = {},
    parameters = {},
    'output-parameters': outputParameters = {},
    secrets = {},
    dependencies = [],
  } = settings
  const jobIds = Object.keys(jobs).filter(id => !deleted?.jobs?.includes(id))
  const forms: Omit<PipelineFormParams, 'branch'>[] = [
    {pipelineId, type: FormType.NAME},
    ...Object.keys(parameters)
      .filter(name => !deleted?.parameters?.includes(name))
      .map(name => ({pipelineId, id: name, type: FormType.PARAMETER})),
    ...Object.keys(outputParameters)
      .filter(name => !deleted?.outputParameters?.includes(name))
      .map(name => ({pipelineId, id: name, type: FormType.OUTPUT_PARAMETER})),
    ...Object.keys(secrets)
      .filter(name => !deleted?.secrets?.includes(name))
      .map(name => ({pipelineId, id: name, type: FormType.SECRET})),
    ...draftIntegrations
      .filter(integration => integration.id && !deleted?.integrations?.includes(integration.id))
      .map(integration => ({pipelineId, id: integration.id, type: FormType.PIPELINE_INTEGRATION})),
    ...dependencies
      .filter(dependency => !deleted?.dependencies?.includes(Object.keys(dependency)[0]))
      .map(dependency => ({pipelineId, id: Object.keys(dependency)[0], type: FormType.DEPENDENCY})),
    ...(vcsRoot != null
      ? [{pipelineId, id: getRepositoryKey(true), type: FormType.REPOSITORY}]
      : []),
    {pipelineId, id: getRepositoryKey(true), type: FormType.TRIGGER},
    ...additionalVcsRoots
      .filter((root, index) => {
        const reference = getVcsRootReference(root)
        return reference
          ? !deleted?.additionalVcsRoots?.includes(reference)
          : !deleted?.incompleteAdditionalVcsRoots?.includes(index)
      })
      .map((_, index) => ({
        pipelineId,
        id: getRepositoryKey(false, index),
        type: FormType.REPOSITORY,
      })),
    ...jobIds.flatMap(jobId => [
      {pipelineId, jobId, type: FormType.NAME},
      ...(jobs[jobId].steps
        ?.map((_, index) => index)
        .filter(index => !deleted?.steps?.[jobId]?.includes(index))
        .map(index => ({pipelineId, jobId, id: index, type: FormType.STEP})) ?? []),
      ...(jobs[jobId].features
        ?.map((feature, index) => ({feature, index}))
        .filter(({feature}) => !feature.deleted)
        .map(({feature, index}) => ({
          pipelineId,
          jobId,
          id: getBuildFeatureFormId(feature.type ?? '', index),
          type: FormType.BUILD_FEATURE,
        })) ?? []),
      ...Object.keys(jobs[jobId].parameters ?? {})
        .filter(name => !deleted?.jobParameters?.includes(name))
        .map(name => ({pipelineId, id: name, type: FormType.JOB_PARAMETER, jobId})),
    ]),
  ]
  return forms.map(form => ({...form, branch}))
}
function getAllJobParameterNames(
  state: State,
  pipelineId: PipelineId,
  branch: string,
  jobId?: string | null,
): string[] {
  const {draft, deleted, renamed} = getPipeline(state, pipelineId, branch) ?? {}
  if (draft == null || !jobId) {
    return []
  }
  const {parameters = {}} = draft.settings?.jobs?.[jobId] ?? {}

  return Object.keys(parameters)
    .filter(name => !deleted?.jobParameters?.includes(name))
    .map(name => renamed?.jobParameters?.[name] ?? name)
}

export const getResolvedPipelineParameters = createSelector(
  (state: State, pipelineId: PipelineId, branch: string) =>
    getDraft(state, pipelineId, branch)?.settings.parameters,
  (state: State, pipelineId: PipelineId, branch: string) =>
    getDeleted(state, pipelineId, branch)?.parameters,
  (state: State, pipelineId: PipelineId, branch: string) =>
    getRenamed(state, pipelineId, branch)?.parameters,
  (parameters, deleted, renamed): Record<string, string | undefined> | undefined => {
    if (parameters == null) {
      return undefined
    }
    const result: Record<string, string | undefined> = {}
    Object.entries(parameters).forEach(([savedName, value]) => {
      if (!deleted?.includes(savedName)) {
        result[renamed?.[savedName] ?? savedName] = value
      }
    })
    return result
  },
)

function getAllParameterNames(state: State, pipelineId: PipelineId, branch: string): string[] {
  const {draft, deleted, renamed} = getPipeline(state, pipelineId, branch) ?? {}
  if (draft == null) {
    return []
  }
  const {parameters} = draft.settings
  return Object.keys(parameters ?? {})
    .filter(name => !deleted?.parameters?.includes(name))
    .map(name => renamed?.parameters?.[name] ?? name)
}

function getAllOutputParameterNames(
  state: State,
  pipelineId: PipelineId,
  branch: string,
): string[] {
  const {draft, deleted, renamed} = getPipeline(state, pipelineId, branch) ?? {}
  if (draft == null) {
    return []
  }
  const outputParameters = draft.settings['output-parameters']
  return Object.keys(outputParameters ?? {})
    .filter(name => !deleted?.outputParameters?.includes(name))
    .map(name => renamed?.outputParameters?.[name] ?? name)
}

function getAllSecretNames(state: State, pipelineId: PipelineId, branch: string): string[] {
  const {draft, deleted, renamed} = getPipeline(state, pipelineId, branch) ?? {}
  if (draft == null) {
    return []
  }
  const {secrets} = draft.settings
  return Object.keys(secrets ?? {})
    .filter(name => !deleted?.secrets?.includes(name))
    .map(name => renamed?.secrets?.[name] ?? name)
}

const getRepositoryByFormId = (
  state: State,
  pipelineId: PipelineId,
  branch: string,
  id: string | number | undefined,
) => {
  const {vcsRoot, additionalVcsRoots} = getDraft(state, pipelineId, branch) ?? {}
  return id === ROOT_REPOSITORY_KEY ? vcsRoot : additionalVcsRoots?.[Number(id)]
}

export function hasActiveRepositories(
  state: State,
  pipelineId: PipelineId,
  branch: string,
): boolean {
  const {vcsRoot, additionalVcsRoots = []} = getDraft(state, pipelineId, branch) ?? {}
  const deleted = getDeleted(state, pipelineId, branch)?.additionalVcsRoots

  return (
    vcsRoot != null ||
    additionalVcsRoots.some(additionalVcsRoot => {
      const reference = getVcsRootReference(additionalVcsRoot)

      return reference != null && !deleted?.includes(reference)
    })
  )
}

function getAllTrackPullRequestsValues(
  state: State,
  pipelineId: PipelineId,
  branch: string,
): boolean[] {
  if (!hasActiveRepositories(state, pipelineId, branch)) {
    return []
  }

  const mainTrackPullRequests =
    getDraft(state, pipelineId, branch)?.vcsRoot?.trackPullRequests ?? false
  const additionalVcsRoots = getDraft(state, pipelineId, branch)?.additionalVcsRoots ?? []
  const values = [mainTrackPullRequests]
  const deleted = getDeleted(state, pipelineId, branch)?.additionalVcsRoots
  if (!additionalVcsRoots) {
    return values
  }
  return additionalVcsRoots
    .filter(vcsRoot => {
      const reference = getVcsRootReference(vcsRoot)
      return reference != null ? !deleted?.includes(reference) : false
    })
    .map(value => value.trackPullRequests ?? false)
    .concat(values)
}

function getVcsTriggerPullRequestValue(
  state: State,
  pipelineId: PipelineId,
  branch: string,
): boolean {
  if (!hasActiveRepositories(state, pipelineId, branch)) {
    return false
  }

  const triggers = getDraft(state, pipelineId, branch)?.triggers
  const triggerPullRequests = triggers?.find(
    trigger => trigger.type === TriggerType.VCS && !trigger.disabled,
  )?.triggerPullRequests

  return triggerPullRequests ?? false
}

function getVcsTriggerBranchFilterValue(
  state: State,
  pipelineId: PipelineId,
  branch: string,
): string {
  if (!hasActiveRepositories(state, pipelineId, branch)) {
    return ALL_BRANCHES
  }

  const triggers = getDraft(state, pipelineId, branch)?.triggers
  const trigger = triggers?.find(trigger => trigger.type === TriggerType.VCS && !trigger.disabled)
  const branchFilter = trigger?.parameters?.branchFilter ?? ''

  return trigger?.triggerBranches ? branchFilter : ALL_BRANCHES
}

export const validatorSelectors: Partial<Record<FormType, Record<string, GetValidators>>> = {
  [FormType.NAME]: {
    name: (
      state,
      {pipelineId, jobId, branch},
      additionalProps: ValidatorAdditionalProps | undefined,
    ) =>
      jobId != null
        ? {
            value: getJobName(state, pipelineId, branch, jobId),
            compareArray: Object.keys(getDraftJobs(state, pipelineId, branch) ?? {}).map(
              otherJobId => getJobName(state, pipelineId, branch, otherJobId),
            ),
            validators: jobNameValidators,
          }
        : {
            value: getDraft(state, pipelineId, branch)?.name,
            relatedArray:
              additionalProps?.pipelineNames
                ?.filter(pipeline => pipeline.pipelineId !== pipelineId)
                .map(pipeline => pipeline.pipelineName) || [],
            validators: pipelineNameValidators,
          },
  },
  [FormType.STEP]: mapValues<typeof errorValidators, Record<string, GetValidators>>(
    errorValidators,
    (validators, fieldName) =>
      (state, {pipelineId, jobId, id, branch}) => {
        const step = getSteps(state, pipelineId, branch, jobId!)?.[Number(id)]
        const value = step?.[fieldName] as string | boolean | undefined

        return {
          value,
          validators,
        }
      },
  ),
  [FormType.JOB_PARAMETER]: {
    name: (state, {pipelineId, jobId, id, branch}) => {
      let value = getRenamed(state, pipelineId, branch)?.jobParameters?.[id!] ?? String(id)

      Object.values(ParameterInputPrefix).forEach(prefix => {
        if (value.startsWith(prefix)) {
          value = value.replace(prefix, '')
        }
      })

      return {
        value,
        compareArray: getAllJobParameterNames(state, pipelineId, branch, jobId),
        relatedArray: getAllSecretNames(state, pipelineId, branch),
        validators: parameterNameValidators,
      }
    },
  },
  [FormType.PARAMETER]: {
    name: (state, {pipelineId, id, branch}) => ({
      value: getRenamed(state, pipelineId, branch)?.parameters?.[id!] ?? String(id),
      compareArray: getAllParameterNames(state, pipelineId, branch),
      relatedArray: getAllSecretNames(state, pipelineId, branch),
      validators: parameterNameValidators,
    }),
    value: (state, {pipelineId, id, branch}) => ({
      value: getDraft(state, pipelineId, branch)?.settings.parameters?.[id!] ?? '',
      validators: parameterValueValidators,
    }),
  },
  [FormType.OUTPUT_PARAMETER]: {
    name: (state, {pipelineId, id, branch}) => ({
      value: getRenamed(state, pipelineId, branch)?.outputParameters?.[id!] ?? String(id),
      compareArray: getAllOutputParameterNames(state, pipelineId, branch),
      validators: parameterNameValidators,
    }),
    value: (state, {pipelineId, id, branch}) => ({
      value: getDraft(state, pipelineId, branch)?.settings['output-parameters']?.[id!] ?? '',
      validators: parameterValueValidators,
    }),
  },
  [FormType.SECRET]: {
    name: (state, {pipelineId, id, branch}) => ({
      value: getRenamed(state, pipelineId, branch)?.secrets?.[id!] ?? String(id),
      compareArray: getAllSecretNames(state, pipelineId, branch),
      relatedArray: getAllParameterNames(state, pipelineId, branch),
      validators: secretNameValidators,
    }),
    value: (state, {pipelineId, id, branch}) => ({
      value: getDraft(state, pipelineId, branch)?.settings.secrets?.[id!] ?? '',
      validators: secretValueValidators,
    }),
  },
  [FormType.TRIGGER]: {
    pullRequests: (state, {pipelineId, branch}) => ({
      value: getVcsTriggerPullRequestValue(state, pipelineId, branch),
      compareArray: getAllTrackPullRequestsValues(state, pipelineId, branch),
      validators: [
        {
          validator: isValueConsistentWithArray,
          message:
            'Invalid auto-run trigger settings: the trigger is set to run on pull requests that the pipeline does not monitor. Ensure the trigger configuration aligns with the pipeline’s “Repository” settings.',
        },
      ],
    }),
    branchFilter: (state, {pipelineId, branch}) => ({
      value: getVcsTriggerBranchFilterValue(state, pipelineId, branch),
      validators: [
        {
          validator: isValueNotEmptyMessage,
          message: 'Branch filter should not be empty',
        },
      ],
    }),
  },
  [FormType.REPOSITORY]: {
    url: (state, {pipelineId, id, branch}) => {
      const {vcsName, isExternal} = getRepositoryByFormId(state, pipelineId, branch, id) ?? {}
      return {
        disabled: isExternal || !getIsGitVcsRoot(vcsName),
        value: getRepositoryByFormId(state, pipelineId, branch, id)?.url,
        validators: [
          {
            validator: isValueNotEmptyMessage,
            message: 'Repository URL should not be empty',
          },
        ],
      }
    },
    // `pipelineBranch` is the pipeline store partition key; the destructured `branch` below is the
    // repository's own git branch (a different concept), so they must not be conflated.
    branch: (state, {pipelineId, id, branch: pipelineBranch}) => {
      const {url, branch, isExternal} =
        getRepositoryByFormId(state, pipelineId, pipelineBranch, id) ?? {}
      return {
        disabled: !url || isExternal,
        value: branch,
        validators: [
          {
            validator: isValueNotEmptyMessage,
            message: 'Branch name should not be empty',
          },
        ],
      }
    },
  },
  [FormType.REPOSITORIES]: {
    pullRequests: (state, {pipelineId, branch}) => ({
      value: getVcsTriggerPullRequestValue(state, pipelineId, branch),
      compareArray: getAllTrackPullRequestsValues(state, pipelineId, branch),
      validators: [
        {
          validator: isValueConsistentWithArray,
          message:
            'Invalid auto-run trigger settings: the trigger is set to run on pull requests that the pipeline does not monitor. Ensure the trigger configuration aligns with the pipeline’s “Repository” settings.',
        },
      ],
    }),
  },
  [FormType.PIPELINE_INTEGRATION]: {
    url: (state, {pipelineId, id, branch}) => {
      const integration = getDraft(state, pipelineId, branch)?.integrations?.find(
        item => item.id === id,
      )
      // Only NPM and Docker integrations have a URL field. Other types (e.g. AmazonDocker, Unity)
      // have none, so the URL must not be validated — otherwise saving the pipeline fails with
      // "Invalid URL" for an integration that has no URL at all.
      // Later we are going to move the validation to the backend. This will eliminate the necessity of
      // hardcoding the types.
      const hasUrlField =
        integration?.type === IntegrationType.NPM || integration?.type === IntegrationType.DOCKER
      const url =
        integration?.parameters?.npmRegistryHost ?? integration?.parameters?.repositoryUrl ?? ''

      // Identify the broken integration by its connection ID (the display name is autogenerated and
      // not configurable, so it would only confuse the user).
      const integrationLabel = id != null ? `the "${id}" integration` : 'the integration'

      return {
        value: url,
        validators: hasUrlField
          ? [
              {
                validator: isValueValidUrl,
                message: `Invalid URL in ${integrationLabel}`,
              },
            ]
          : [],
      }
    },
  },
  [FormType.DEPENDENCY]: {
    dependency: (_, {pipelineId, id}) => ({
      value: id === pipelineId ? '' : id?.toString(),
      validators: [
        {
          validator: isValueNotEmptyMessage,
          message: 'Dependency should not be empty',
        },
      ],
    }),
  },
}

export const getPipelineErrors = (state: State, filter: PipelineError) =>
  state.pipelines.pipelineErrors.filter(matchError(filter))

export const getIsPipelineValid = createSelector(
  [
    (state: State, props: {pipelineId: PipelineId; branch: string}) =>
      !getYamlDraft(state, props.pipelineId, props.branch)?.diagnostics?.length,
    (state: State, props: {pipelineId: PipelineId; branch: string}) =>
      getYamlValidity(state, props.pipelineId, props.branch)?.isParsed ?? true,
  ],
  (isValid, isParsed) => isValid && isParsed,
)

export const getHasStoredInRepoBeenEnabled = (
  state: State,
  pipelineId: PipelineId,
  branch: string,
) =>
  Boolean(
    !getOriginal(state, pipelineId, branch)?.versionedSettings?.storedInRepo &&
    getDraft(state, pipelineId, branch)?.versionedSettings?.storedInRepo,
  )

export const getHasStoredInRepoBeenDisabled = (
  state: State,
  pipelineId: PipelineId,
  branch: string,
) =>
  Boolean(
    getOriginal(state, pipelineId, branch)?.versionedSettings?.storedInRepo &&
    !getDraft(state, pipelineId, branch)?.versionedSettings?.storedInRepo,
  )

export const getDebug = (state: State, id: PipelineId, branch: string) =>
  getDebugEntry(state.pipelines.debug, id, branch)

export const getIsDebugRunning = (state: State, pipelineId: PipelineId, branch: string) =>
  getDebug(state, pipelineId, branch)?.isRunning

export const getDrafParamtersChangesWatcher = createSelector(
  [
    (state: State, pipelineId: PipelineId, branch: string) =>
      getPipeline(state, pipelineId, branch)?.added?.parameters,
    (state: State, pipelineId: PipelineId, branch: string) =>
      getPipeline(state, pipelineId, branch)?.deleted?.parameters,
  ],
  (addedJobParameters, deletedJobParameters) => [
    ...Object.keys(addedJobParameters ?? {}),
    ...Object.keys(deletedJobParameters ?? {}),
  ],
)

export const getStringificationError = (state: State, pipelineId: PipelineId, branch: string) => {
  const resolvedSettings = getResolvedSettings(state, pipelineId, branch)
  return resolvedSettings?.stringificationError
}
